import { call, put, select, takeLatest } from 'redux-saga/effects';
import {
  FETCH_DASHBOARD_ANALYTICS,
  fetchDashboardAnalyticsSuccess,
  fetchDashboardAnalyticsFail,
  fetchDashboardAnalyticsSkip,
} from '../actions/dashboard';
import { fetchAnalyticsData } from '../apis/dashboard';
import {
  buildDashboardCacheKey,
  isDashboardCacheFresh,
  processDashboardAnalytics,
  yieldToBrowser,
} from '../../utils/dashboardAnalytics';

const selectDashboardState = (state) => state.dashboard || {};

const hasDayWiseEntries = (dayWiseData) =>
  Boolean(dayWiseData && Object.keys(dayWiseData).length > 0);

function* fetchDashboardAnalytics(action) {
  const {
    startDate,
    endDate,
    isSingleDay,
    rangeStart,
    rangeEnd,
    forceRefresh = false,
    isMultiDay = false,
  } = action.payload;

  const cacheKey = buildDashboardCacheKey(startDate, endDate);

  try {
    const dashboard = yield select(selectDashboardState);
    const cache = dashboard.cache || {};

    // Already showing this exact range with complete data — do nothing
    if (
      !forceRefresh &&
      dashboard.activeCacheKey === cacheKey &&
      dashboard.eplTchData &&
      (!isMultiDay || hasDayWiseEntries(dashboard.dayWiseData))
    ) {
      yield put(fetchDashboardAnalyticsSkip());
      return;
    }

    if (!forceRefresh) {
      const cachedEntry = cache[cacheKey];
      const cacheHasDayWise = hasDayWiseEntries(cachedEntry?.dayWiseData);
      // Never treat multi-day cache with missing day-wise as fresh (race leftover)
      const cacheComplete =
        Boolean(cachedEntry?.eplTchData) && (!cachedEntry?.isMultiDay || cacheHasDayWise);

      if (cachedEntry && isDashboardCacheFresh(cachedEntry) && cacheComplete) {
        yield put(
          fetchDashboardAnalyticsSuccess({
            cacheKey,
            eplTchData: cachedEntry.eplTchData,
            dayWiseData: cachedEntry.dayWiseData || {},
            fromCache: true,
            fetchedAt: cachedEntry.fetchedAt,
            isMultiDay: Boolean(cachedEntry.isMultiDay),
            dayWisePending: false,
            alreadyActive:
              dashboard.activeCacheKey === cacheKey &&
              (!cachedEntry.isMultiDay || hasDayWiseEntries(dashboard.dayWiseData)),
          })
        );
        return;
      }
    }

    const rawData = yield call(fetchAnalyticsData, { startDate, endDate, isSingleDay });

    yield call(yieldToBrowser);

    const { filteredData, dayWiseData } = yield call(processDashboardAnalytics, {
      rawData,
      startDate,
      endDate,
      isSingleDay,
      rangeStart,
      rangeEnd,
    });

    const fetchedAt = Date.now();

    // Always include day-wise in the same success put. A deferred second action was
    // cancelled by takeLatest when the empty dayWise update re-triggered fetch.
    yield put(
      fetchDashboardAnalyticsSuccess({
        cacheKey,
        eplTchData: filteredData,
        dayWiseData: isMultiDay ? dayWiseData : {},
        fromCache: false,
        fetchedAt,
        isMultiDay,
        dayWisePending: false,
      })
    );
  } catch (error) {
    yield put(fetchDashboardAnalyticsFail(error.message || 'Error fetching dashboard analytics'));
  }
}

export default function* dashboardSaga() {
  yield takeLatest(FETCH_DASHBOARD_ANALYTICS, fetchDashboardAnalytics);
}
